<DOMContainer width="100%" height="100%">
    <div class="rpg-ui-save-load-layer" click={closeSaveLoadFromPointer}>
        <div class="rpg-ui-save-load rpg-anim-fade-in" click={stopEvent}>
            <button class="rpg-ui-close-button" type="button" aria-label={t("rpg.ui.close")} click={closeSaveLoadFromPointer}>×</button>
            <div class="rpg-ui-save-load-header">
                <div class="rpg-ui-save-load-title">{title()}</div>
                <div class="rpg-ui-save-load-subtitle">{subtitle()}</div>
            </div>
            <Navigation tabindex={selectedSlot} controls={controls}>
                <div class="rpg-ui-save-load-list">
                    @for ((item,displayIndex) of displaySlots) {
                        <div
                            class="rpg-ui-save-load-slot"
                            class={{active: selectedSlot() === displayIndex}}
                            tabindex={displayIndex}
                            data-slot-index={displayIndex}
                            click={selectSlot(displayIndex)}
                        >
                            <div class="rpg-ui-save-load-slot-index">{item.label}</div>
                            @if (item.slot) {
                                <div class="rpg-ui-save-load-slot-meta">
                                    <div class="rpg-ui-save-load-slot-line">{t("rpg.save.level")}: {item.slot.level ?? "-"}</div>
                                    <div class="rpg-ui-save-load-slot-line">{t("rpg.save.exp")}: {item.slot.exp ?? "-"}</div>
                                    <div class="rpg-ui-save-load-slot-line">{t("rpg.save.map")}: {item.slot.map ?? "-"}</div>
                                    <div class="rpg-ui-save-load-slot-line">{t("rpg.save.date")}: {item.slot.date ?? "-"}</div>
                                </div>
                            }
                            @else {
                                <div class="rpg-ui-save-load-slot-empty">{t("rpg.save.empty-slot")}</div>
                            }
                        </div>
                    }
                </div>
            </Navigation>
        </div>
    </div>
</DOMContainer>

<script>
    import { signal, computed, effect, createTabindexNavigator, mount } from "canvasengine";
    import { inject } from "../../core/inject";
    import { RpgClientEngine } from "../../RpgClientEngine";
    import { SaveClientService } from "../../services/save";
    import { PrebuiltGui } from "@rpgjs/common";
    import { RpgGui } from "../../Gui/Gui";
    import { getKeyboardControlBind } from "../../services/actionInput";
    import { menuGamepadControls } from "../../services/gamepadControls";

    const engine = inject(RpgClientEngine);
    const { t } = engine.i18n();
    const saveClient = inject(SaveClientService);
    const gui = inject(RpgGui);
    const keyboardControls = engine.globalConfig.keyboardControls;

    const selectedSlot = signal(0);
    const DEFAULT_SLOTS = 4;
    const defaultSlots = () => Array.from({ length: DEFAULT_SLOTS }, () => null);
    const localSlots = signal(defaultSlots());

    const { data, onFinish } = defineProps();

    const title = computed(() => data().mode === "save" ? t("rpg.save.title") : t("rpg.load.title"));
    const subtitle = computed(() => data().mode === "save"
        ? t("rpg.save.subtitle")
        : t("rpg.load.subtitle")
    );

    const slotsValue = computed(() => localSlots());
    const displaySlots = computed(() => {
        const slots = slotsValue();
        const items = slots.map((slot, index) => ({
            kind: "slot",
            slot,
            slotIndex: index,
            label: t("rpg.save.slot", { index: index + 1 }),
            readonly: false
        }));
        if (!data().showAutoSlot) return items;
        const index = typeof data().autoSlotIndex === "number" ? data().autoSlotIndex : 0;
        const autoSlot = slots[index] ?? null;
        const readonly = data().mode === "save";
        return [
            {
                kind: "auto",
                slot: autoSlot,
                slotIndex: index,
                label: data().autoSlotLabel || t("rpg.save.auto"),
                readonly
            },
            ...items
        ];
    });
    const nav = createTabindexNavigator(selectedSlot, { count: () => displaySlots().length }, "wrap");

    const canSelect = (item) => {
        if (item.readonly) return false;
        if (data().mode === "load" && !item.slot) return false;
        return true;
    };

    const normalizeSlotValue = (value) => {
        if (!value) return null;
        if (typeof value === "string") {
            try {
                return JSON.parse(value);
            } catch {
                return null;
            }
        }
        return value;
    };

    const normalizeSlots = (list) => {
        if (Array.isArray(list)) {
            const length = Math.max(list.length, DEFAULT_SLOTS);
            return Array.from({ length }, (_, index) => normalizeSlotValue(list[index]));
        }
        if (!list || typeof list !== "object") {
            return defaultSlots();
        }
        const keys = Object.keys(list).filter((key) => /^\d+$/.test(key));
        const indices = keys.map((key) => Number(key)).filter((index) => index >= 0);
        const maxIndex = indices.length ? Math.max(...indices) : -1;
        const length = Math.max(maxIndex + 1, DEFAULT_SLOTS);
        const slots = Array.from({ length }, () => null);
        keys.forEach((key) => {
            const index = Number(key);
            if (index < 0 || index >= length) return;
            slots[index] = normalizeSlotValue(list[key]);
        });
        return slots;
    };

    const hasProvidedSlots = (list) => {
        if (Array.isArray(list)) return list.length > 0;
        return !!(list && typeof list === "object" && Object.keys(list).length > 0);
    };

    const refreshSlots = async () => {
        try {
            const list = await saveClient.listSlots();
            localSlots.set(normalizeSlots(list));
        } catch (err) {
            localSlots.set(defaultSlots());
        }
    };

    const triggerSelect = async (displayIndex) => {
        const list = displaySlots();
        const item = list[displayIndex];
        if (!item) return;
        if (!canSelect(item)) {
            engine.playUiSound("error");
            return;
        }
        engine.playUiSound("confirm");
        const action = data().mode === "save" ? "save" : "load";
        if (action === "save") {
            await saveClient.saveSlot(item.slotIndex);
            await refreshSlots();
        }
        if (action === "load") {
            await saveClient.loadSlot(item.slotIndex);
        }
        setTimeout(() => {
            if (onFinish) onFinish();
            gui.hide(PrebuiltGui.Save);
            gui.hide(PrebuiltGui.TitleScreen);
        }, 50);

    };

    function selectSlot(index) {
        return function() {
            selectedSlot.set(index);
            engine.playUiSound("navigate");
            triggerSelect(index);
        }
    }

    const stopEvent = (event) => {
        event?.stopPropagation?.();
    };

    const closeSaveLoad = () => {
        engine.playUiSound("close");
        if (onFinish) onFinish();
        gui.hide(PrebuiltGui.Save);
    };

    const closeSaveLoadFromPointer = (event) => {
        stopEvent(event);
        closeSaveLoad();
    };

    const controls = signal({
        up: {
            repeat: true,
            bind: keyboardControls.up,
            throttle: 150,
            keyDown() {
                nav.next(-1);
                engine.playUiSound("navigate");
            }
        },
        down: {
            repeat: true,
            bind: keyboardControls.down,
            throttle: 150,
            keyDown() {
                nav.next(1);
                engine.playUiSound("navigate");
            }
        },
        action: {
            bind: getKeyboardControlBind(keyboardControls.action),
            keyDown() {
                triggerSelect(selectedSlot());
            }
        },
        escape: {
            bind: keyboardControls.escape,
            keyDown() {
                closeSaveLoad();
            }
        },
        gamepad: menuGamepadControls
    });

    mount((element) => {
        engine.playUiSound("open");
        refreshSlots();
    });
</script>
